import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Fade,
  Paper,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import LoadingImage from '../../components/LoadingImage';

const TirDorseYedekParca: React.FC = () => {
  useEffect(() => {
    document.title = "Tır ve Frigo Dorse Yedek Parça Seçimi Kriterleri - Kale Treyler";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de tır ve frigo dorse yedek parça tedariği. Kasa kapı kilitleri, rüzgarlık, çamurluk, cıvata ve hırdavat malzemeleri seçim ipuçları.");
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "tır yedek parça Akdeniz, dorse yedek parçaları Mersin, frigo kasa kapı kilidi, çamurluk ve rüzgarlık, dorse hırdavat cıvata");
    }

    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/blog/tir-dorse-yedek-parca-secimi');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  return (
    <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowBack />}
              sx={{ mb: 4, textTransform: 'none', fontWeight: 'bold' }}
            >
              Blog Yazılarına Dön
            </Button>

            <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'grey.200', borderRadius: 4 }}>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                Yedek Parça
              </Typography>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, mt: 1, mb: 4, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.2 }}>
                Tır ve Dorse Yedek Parça Seçiminde Kalite ve Standart Kriterleri
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Yayınlanma Tarihi: <strong>8 Temmuz 2026</strong>
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Yazar: <strong>Kale Treyler Editör</strong>
                </Typography>
              </Box>

              <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <LoadingImage
                  src="/42.png"
                  alt="Tır ve Dorse Yedek Parça ve Aksesuarları"
                  style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }}
                />
              </Box>

              <Box sx={{ typography: 'body1', color: 'text.primary', lineHeight: 1.8 }}>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Tır ve dorse gibi ağır vasıta araçlar, binlerce kilometrelik uzun yol şartlarında ve ağır yük tonajları altında çalışır. Bu çalışma ortamı, mekanik aksamların, kapı mekanizmalarının ve dış koruma plastiklerinin zamanla aşınmasına ve yıpranmasına yol açar. Yıpranan parçaların zamanında ve doğru standarttaki yedek parçalarla değiştirilmesi, hem sürüş güvenliği hem de yolda kalma maliyetlerinin önlenmesi açısından kritiktir. Mersin Akdeniz bölgesinde faaliyet gösteren <RouterLink to="/hizmetler/yedek-parca" style={{ color: '#1e3a8a', fontWeight: 'bold', textDecoration: 'underline' }}>yedek parça</RouterLink> departmanımızda, araçlarınız için uyumlu parça tedariği sağlanmaktadır.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Frigo Kasa Kapı Kilit Sistemleri ve Aksesuarlar
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Frigo dorse kasalarında yalıtımın korunması yalnızca poliüretan panellere değil, aynı zamanda kapıların sımsıkı kapanmasını sağlayan mekanik aksamlara da bağlıdır. Aşınmış veya ayarı bozulmuş bir <strong>frigo kasa kapı kilidi</strong>, kapının tam kapanmasını önleyerek içeri sıcak hava girmesine sebep olur. Bu nedenle menteşeler, kapı kolları, kilit dilleri ve lastik contalar gibi aksesuarların kalitesi doğrudan taşımacılık kalitesini etkiler. Servisimiz bünyesinde uluslararası standartlara uygun kilit sistemlerinin tedariğini ve montajını yapıyoruz.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Dış Koruma Elemanları: Çamurluk ve Rüzgarlık Parçaları
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Ağır vasıta araçlarda aerodinamik yapıyı iyileştirmek ve tekerleklerden savrulan çamur/su gibi dış etkenleri engellemek için dış plastik aksamlar kullanılır. Kaliteli bir <strong>çamurluk ve rüzgarlık</strong> kullanımı, aracın rüzgar direncini azaltarak yakıt ekonomisine olumlu etki eder. Mersin bölgesindeki tır parklarında bekleyen araçların ihtiyaç duyduğu plastik çamurluklar, tampon parçaları ve dorse etek aksesuarları geniş yedek parça stoklarımızda yer almaktadır.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Akdeniz Bölgesi Dorse Yedek Parça İhtiyaçları
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                  Mersin limanı ve lojistik merkezlerindeki araç trafiği, hızlı yedek parça tedariğini zorunlu kılmaktadır. Doğru hırdavat ve kaliteli cıvata grupları kullanılmadan yapılan montajlar, parçaların yolda gevşemesine veya düşmesine yol açabilir. Bu yüzden sunduğumuz <strong>tır yedek parça Akdeniz</strong> ve <strong>dorse yedek parçaları Mersin</strong> hizmetlerimizle, araçlarınızın tamir ve bakım süreçlerinde mukavemeti yüksek cıvata, silikon sızdırmazlık ve hırdavat malzemeleri kullanılmasına özen gösteriyoruz.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 1, bgcolor: 'grey.100' }}>
                  Not: Sitemizde yer alan tüm blog içerikleri lojistik sektörü çalışanları için genel bilgilendirme amacıyla hazırlanmıştır. Aracınızın marka ve modeline en uygun yedek parçalar için lütfen stok durumunu sorgulayınız.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default TirDorseYedekParca;
